import React, { useState, useEffect } from 'react'
import { CheckCircle, XCircle, Search, Building2, Mail, Phone, RefreshCw, X, Globe, User, LayoutGrid, LayoutList, Eye, Calendar, Briefcase, Award, MapPin, FileText, Users, ChevronDown, ChevronLeft, ChevronRight, Star } from 'lucide-react'
import { supabaseClient } from '../config/supabase'
import { useAdminScope } from '../hooks/useAdminScope'
import { useLocation } from 'react-router-dom'
import type { PendingEmployerPartnership } from '../types'
import toast from 'react-hot-toast'
import { notifyPartnershipApprovedEmail, notifyPartnershipRejectedEmail } from '../utils/emailNotify'

const EMPLOYERS_PER_PAGE = 12

type Tab = 'all' | 'pending'

interface AllEmployer {
  user_id: string
  company_name: string
  company_industry: string
  company_website?: string
  company_logo?: string
  company_description?: string
  company_location?: string
  company_size?: string
  contact_person?: string
  position?: string
  phone?: string
  email: string
  approval_status: string
  is_industry_partner: boolean
  profile_picture?: string
  created_at: string
  partner_depts: string[]
  avg_rating?: number | null
  rating_count?: number | null
}

interface GroupedPending {
  company_id: string
  company_name: string
  company_industry: string
  contact_person?: string
  contact_email: string
  phone?: string
  company_website?: string
  requests: Array<{
    partnership_id: string
    department_name: string
    requested_at: string
  }>
}

const ManageEmployers: React.FC = () => {
  const location = useLocation()
  const { isSuperAdmin, allowedDepartments, loading: scopeLoading } = useAdminScope()
  const [tab, setTab] = useState<Tab>(location.state?.tab === 'pending' ? 'pending' : 'all')
  const [allEmployers, setAllEmployers] = useState<AllEmployer[]>([])
  const [pending, setPending] = useState<PendingEmployerPartnership[]>([])
  const [loading, setLoading] = useState(true)
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  // Approve modal
  const [approveTarget, setApproveTarget] = useState<PendingEmployerPartnership | null>(null)

  // Reject modal
  const [rejectTarget, setRejectTarget] = useState<PendingEmployerPartnership | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectLoading, setRejectLoading] = useState(false)

  // View modal
  const [viewTarget, setViewTarget] = useState<AllEmployer | null>(null)

  // Deactivate modal
  const [deactivateTarget, setDeactivateTarget] = useState<AllEmployer | null>(null)
  const [deactivateLoading, setDeactivateLoading] = useState(false)
  const [deactivateReason, setDeactivateReason] = useState('')
  const [employerView, setEmployerView] = useState<'table' | 'card'>('table')
  const [empPage, setEmpPage] = useState(1)
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set())

  const toggleCompanyExpand = (company_id: string) => {
    setCollapsedCompanies(prev => {
      const s = new Set(prev)
      if (s.has(company_id)) s.delete(company_id)
      else s.add(company_id)
      return s
    })
  }

  useEffect(() => {
    if (!scopeLoading) {
      fetchData()
    }
  }, [scopeLoading, isSuperAdmin, allowedDepartments])

  const fetchData = async () => {
    setLoading(true)
    try {
      await Promise.all([fetchAllEmployers(), fetchPending()])
    } finally {
      setLoading(false)
    }
  }

  const fetchAllEmployers = async () => {
    try {
      if (isSuperAdmin) {
        // Superadmin: all approved employers on the platform
        const [{ data, error }, { data: deptData }, { data: ratingsData }] = await Promise.all([
          supabaseClient
            .from('employer_profiles')
            .select('user_id, company_id, contact_person, phone, position, is_industry_partner, approval_status, created_at, profile_picture, companies(name, industry, website, logo_url, description, location, company_size_text), users!employer_profiles_user_id_fkey(email)')
            .order('created_at', { ascending: false }),
          supabaseClient
            .from('company_department_partnerships')
            .select('company_id, departments(name, abbreviation)')
            .eq('status', 'approved'),
          supabaseClient.rpc('get_company_public_ratings'),
        ])
        if (error) throw error

        // Build company_id → ratings map
        const ratingsMap = new Map<string, { avg_rating: number; rating_count: number }>()
        for (const r of (ratingsData || []) as Array<{ company_id: string; avg_rating: number; rating_count: number }>) {
          ratingsMap.set(r.company_id, r)
        }

        // Build company_id → dept abbreviations map
        const deptMap: Record<string, string[]> = {}
        for (const row of (deptData || []) as any[]) {
          const cid = row.company_id
          const abbr = row.departments?.abbreviation || row.departments?.name
          if (cid && abbr) {
            if (!deptMap[cid]) deptMap[cid] = []
            if (!deptMap[cid].includes(abbr)) deptMap[cid].push(abbr)
          }
        }

        setAllEmployers(
          (data || []).map((r: any) => ({
            user_id: r.user_id,
            company_name: r.companies?.name || '—',
            company_industry: r.companies?.industry || '—',
            company_website: r.companies?.website,
            company_logo: r.companies?.logo_url,
            company_description: r.companies?.description,
            company_location: r.companies?.location,
            company_size: r.companies?.company_size_text,
            contact_person: r.contact_person,
            position: r.position,
            phone: r.phone,
            email: r.users?.email || '—',
            approval_status: r.approval_status || 'approved',
            is_industry_partner: r.is_industry_partner || false,
            profile_picture: r.profile_picture,
            created_at: r.created_at,
            partner_depts: r.is_industry_partner ? (deptMap[r.company_id] || []) : [],
            avg_rating: ratingsMap.get(r.company_id)?.avg_rating ?? null,
            rating_count: ratingsMap.get(r.company_id)?.rating_count ?? null,
          }))
        )
      } else if (allowedDepartments && allowedDepartments.length > 0) {
        // Dept admin: only their approved industry partners
        const [{ data, error }, { data: ratingsData }] = await Promise.all([
          supabaseClient
            .from('company_department_partnerships')
            .select(`
              created_at,
              departments(name, abbreviation),
              companies(
                id, name, industry, website, logo_url, description, location, company_size_text,
                employer_profiles(
                  user_id, contact_person, phone, position, is_industry_partner, approval_status, created_at, profile_picture,
                  users!employer_profiles_user_id_fkey(email)
                )
              )
            `)
            .eq('status', 'approved')
            .in('departments.name', allowedDepartments),
          supabaseClient.rpc('get_company_public_ratings'),
        ])
        if (error) throw error

        // Build company_id → ratings map
        const ratingsMap = new Map<string, { avg_rating: number; rating_count: number }>()
        for (const r of (ratingsData || []) as Array<{ company_id: string; avg_rating: number; rating_count: number }>) {
          ratingsMap.set(r.company_id, r)
        }

        // Build dept abbreviation list per user_id before dedup
        const deptsByUserId: Record<string, string[]> = {}
        for (const row of (data || []) as any[]) {
          if (!row.departments) continue
          const ep = row.companies?.employer_profiles?.[0]
          if (!ep) continue
          const abbr = row.departments.abbreviation || row.departments.name
          if (!deptsByUserId[ep.user_id]) deptsByUserId[ep.user_id] = []
          if (!deptsByUserId[ep.user_id].includes(abbr)) deptsByUserId[ep.user_id].push(abbr)
        }

        const results: AllEmployer[] = []
        for (const row of (data || []) as any[]) {
          if (!row.departments) continue // not in allowed depts
          const ep = row.companies?.employer_profiles?.[0]
          if (!ep) continue
          const companyId: string = row.companies?.id
          results.push({
            user_id: ep.user_id,
            company_name: row.companies?.name || '—',
            company_industry: row.companies?.industry || '—',
            company_website: row.companies?.website,
            company_logo: row.companies?.logo_url,
            company_description: row.companies?.description,
            company_location: row.companies?.location,
            company_size: row.companies?.company_size_text,
            contact_person: ep.contact_person,
            position: ep.position,
            phone: ep.phone,
            email: ep.users?.email || '—',
            approval_status: ep.approval_status || 'approved',
            is_industry_partner: true,
            profile_picture: ep.profile_picture,
            created_at: ep.created_at,
            partner_depts: deptsByUserId[ep.user_id] || [],
            avg_rating: ratingsMap.get(companyId)?.avg_rating ?? null,
            rating_count: ratingsMap.get(companyId)?.rating_count ?? null,
          })
        }
        // Deduplicate by user_id (company may partner with multiple depts)
        const seen = new Set<string>()
        setAllEmployers(results.filter(r => {
          if (seen.has(r.user_id)) return false
          seen.add(r.user_id)
          return true
        }))
      } else {
        setAllEmployers([])
      }
    } catch (err: any) {
      console.error('Error fetching all employers:', err)
    }
  }

  const fetchPending = async () => {
    try {
      if (isSuperAdmin) {
        const { data, error } = await supabaseClient.rpc('get_all_pending_partnerships')
        if (error) throw error
        setPending(
          (data || []).map((r: any) => ({
            partnership_id: r.partnership_id,
            company_id: r.company_id,
            company_name: r.company_name,
            company_industry: r.company_industry,
            company_website: r.company_website,
            department_name: r.department_name,
            contact_person: r.contact_person,
            phone: r.phone,
            contact_email: r.contact_email,
            requested_at: r.requested_at,
          }))
        )
      } else if (allowedDepartments && allowedDepartments.length > 0) {
        const all: PendingEmployerPartnership[] = []
        for (const dept of allowedDepartments) {
          const { data, error } = await supabaseClient.rpc('get_pending_partnerships_for_department', { dept_name: dept })
          if (error) { console.error(`Error fetching for ${dept}:`, error); continue }
          if (data) {
            all.push(...(data as any[]).map((r: any) => ({
              partnership_id: r.partnership_id,
              company_id: r.company_id,
              company_name: r.company_name,
              company_industry: r.company_industry,
              company_website: r.company_website,
              department_name: r.department_name,
              contact_person: r.contact_person,
              phone: r.phone,
              contact_email: r.contact_email,
              requested_at: r.requested_at,
            })))
          }
        }
        setPending(all)
      } else {
        setPending([])
      }
    } catch (err: any) {
      console.error('Error fetching pending partnerships:', err)
      const msg = err?.message || err?.error_description || JSON.stringify(err)
      toast.error(`Failed to load pending partnerships: ${msg}`, { duration: 8000 })
    }
  }

  const handleApprove = async () => {
    if (!approveTarget) return
    const p = approveTarget
    setApproveTarget(null)
    setProcessingIds(prev => new Set(prev).add(p.partnership_id))
    try {
      const { error } = await supabaseClient.rpc('approve_employer_partnership', {
        partnership_id: p.partnership_id,
      })
      if (error) throw error
      toast.success(`${p.company_name} approved as partner for ${p.department_name}!`)
      notifyPartnershipApprovedEmail(p.company_id, p.department_name)
      setPending(prev => prev.filter(x => x.partnership_id !== p.partnership_id))
      await fetchAllEmployers()
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve partnership')
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(p.partnership_id); return s })
    }
  }

  const openRejectModal = (p: PendingEmployerPartnership) => {
    setRejectTarget(p)
    setRejectReason('')
  }

  const handleReject = async () => {
    if (!rejectTarget) return
    setRejectLoading(true)
    try {
      const { error } = await supabaseClient.rpc('reject_employer_partnership', {
        partnership_id: rejectTarget.partnership_id,
        reason: rejectReason.trim() || null,
      })
      if (error) throw error
      toast.success(`${rejectTarget.company_name}'s request for ${rejectTarget.department_name} was rejected.`)
      notifyPartnershipRejectedEmail(rejectTarget.company_id, rejectTarget.department_name)
      setPending(prev => prev.filter(x => x.partnership_id !== rejectTarget.partnership_id))
      setRejectTarget(null)
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject partnership')
    } finally {
      setRejectLoading(false)
    }
  }

  const handleDeactivate = async () => {
    if (!deactivateTarget) return
    const target = deactivateTarget
    const newStatus = target.approval_status === 'deactivated' ? 'approved' : 'deactivated'
    const reason = newStatus === 'deactivated' ? (deactivateReason.trim() || null) : null
    setDeactivateTarget(null)
    setDeactivateReason('')
    setDeactivateLoading(true)
    try {
      const { error } = await supabaseClient
        .from('employer_profiles')
        .update({ approval_status: newStatus, deactivation_reason: reason })
        .eq('user_id', target.user_id)
      if (error) throw error
      toast.success(`${target.company_name} has been ${newStatus === 'deactivated' ? 'deactivated' : 'reactivated'}.`)
      await fetchAllEmployers()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update account status')
    } finally {
      setDeactivateLoading(false)
    }
  }

  // Reset employer page when search changes
  useEffect(() => { setEmpPage(1) }, [searchTerm])

  // Filtered lists
  const filteredAll = allEmployers.filter(e =>
    !searchTerm ||
    e.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.company_industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.contact_person || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  const empTotalPages = Math.ceil(filteredAll.length / EMPLOYERS_PER_PAGE)
  const empStartIndex = (empPage - 1) * EMPLOYERS_PER_PAGE
  const pagedAll = filteredAll.slice(empStartIndex, empStartIndex + EMPLOYERS_PER_PAGE)

  const filteredPending = pending.filter(p =>
    !searchTerm ||
    p.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.company_industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.department_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const pendingCompanyCount = new Set(pending.map(p => p.company_id)).size

  const groupedPending: GroupedPending[] = (() => {
    const map = new Map<string, GroupedPending>()
    for (const p of filteredPending) {
      if (!map.has(p.company_id)) {
        map.set(p.company_id, {
          company_id: p.company_id,
          company_name: p.company_name,
          company_industry: p.company_industry,
          contact_person: p.contact_person,
          contact_email: p.contact_email,
          phone: p.phone,
          company_website: p.company_website,
          requests: [],
        })
      }
      map.get(p.company_id)!.requests.push({
        partnership_id: p.partnership_id,
        department_name: p.department_name,
        requested_at: p.requested_at,
      })
    }
    return Array.from(map.values())
  })()

  if (scopeLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Manage Employers</h1>
              <p className="text-blue-200 text-sm mt-1">
                {isSuperAdmin ? 'All employers on the platform' : `Industry partners for: ${allowedDepartments?.join(', ')}`}
              </p>
            </div>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {([
          { key: 'all', label: `All Employers (${allEmployers.length})` },
          { key: 'pending', label: `Pending (${pendingCompanyCount})` },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === key ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search companies, departments..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {tab === 'all' && (
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg flex-shrink-0">
            <button
              onClick={() => setEmployerView('table')}
              title="Table view"
              className={`p-1.5 rounded-md transition-colors ${
                employerView === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutList className="w-4 h-4" />
            </button>
            <button
              onClick={() => setEmployerView('card')}
              title="Card view"
              className={`p-1.5 rounded-md transition-colors ${
                employerView === 'card' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* ── All Employers tab ── */}
      {tab === 'all' && (
        <div>
          {filteredAll.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No employers registered yet.</p>
            </div>
          ) : employerView === 'card' ? (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {pagedAll.map((e, i) => (
                <div key={e.user_id || i} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col items-center text-center overflow-hidden">
                  {/* Thin yellow top accent */}
                  <div className="w-full h-1 bg-yellow-400 flex-shrink-0" />
                  <div className="flex flex-col items-center px-5 pt-5 pb-5 w-full">
                    {/* Profile avatar (prominent) + company logo badge */}
                    <div className="flex items-end justify-center mb-3 relative">
                      {/* Contact person avatar — large */}
                      <div className="w-16 h-16 rounded-full bg-blue-50 border-2 border-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {e.profile_picture ? (
                          <img src={e.profile_picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-7 h-7 text-blue-300" />
                        )}
                      </div>
                      {/* Company logo — small badge bottom-right */}
                      {e.company_logo && (
                        <div className="w-7 h-7 rounded-md bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 absolute -bottom-1 -right-1 shadow-sm">
                          <img src={e.company_logo} alt="" className="w-full h-full object-contain p-0.5" />
                        </div>
                      )}
                    </div>
                    {e.contact_person
                      ? <p className="font-bold text-gray-900 text-sm leading-tight">{e.contact_person}</p>
                      : <p className="font-bold text-gray-900 text-sm leading-tight">{e.company_name}</p>
                    }
                    <p className="text-xs text-gray-500 mt-0.5">{e.company_name}</p>
                    {e.avg_rating != null && (
                      <div className="flex items-center justify-center gap-1 mt-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className="w-3 h-3"
                            fill={s <= Math.round(e.avg_rating!) ? '#FBBF24' : 'none'}
                            stroke={s <= Math.round(e.avg_rating!) ? '#FBBF24' : '#D1D5DB'} />
                        ))}
                        <span className="text-xs text-gray-400 ml-0.5">{Number(e.avg_rating).toFixed(1)}</span>
                        {e.rating_count != null && (
                          <span className="text-xs text-gray-400">({e.rating_count})</span>
                        )}
                      </div>
                    )}
                    <span className="text-xs text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full mt-1.5">{e.company_industry}</span>
                    <div className="flex flex-wrap gap-1 justify-center mt-1.5">
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        e.approval_status === 'approved'    ? 'bg-green-100 text-green-700' :
                        e.approval_status === 'pending'     ? 'bg-yellow-100 text-yellow-700' :
                        e.approval_status === 'deactivated' ? 'bg-gray-100 text-gray-500' :
                                                              'bg-red-100 text-red-700'
                      }`}>
                        {e.approval_status.charAt(0).toUpperCase() + e.approval_status.slice(1)}
                      </span>
                      {e.is_industry_partner && (
                        <span className="text-xs font-semibold bg-yellow-400 text-gray-900 px-2.5 py-0.5 rounded-full">Partner</span>
                      )}
                    </div>
                    {e.is_industry_partner && e.partner_depts.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center mt-1">
                        {e.partner_depts.map(d => (
                          <span key={d} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded-full">{d}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-2 truncate w-full">{e.email}</p>
                    {e.phone && <p className="text-xs text-gray-400">{e.phone}</p>}
                    <div className="mt-4 flex gap-2 w-full">
                      <button
                        onClick={() => setViewTarget(e)}
                        className="flex-1 text-xs font-semibold px-3 py-2 rounded-xl border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                      <button
                        onClick={() => setDeactivateTarget(e)}
                        className={`flex-1 text-xs font-semibold px-3 py-2 rounded-xl transition-colors ${
                          e.approval_status === 'deactivated'
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'border border-red-200 text-red-600 hover:bg-red-50'
                        }`}
                      >
                        {e.approval_status === 'deactivated' ? 'Reactivate' : 'Deactivate'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {filteredAll.length > EMPLOYERS_PER_PAGE && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{empStartIndex + 1}</span>–<span className="font-medium">{Math.min(empStartIndex + EMPLOYERS_PER_PAGE, filteredAll.length)}</span> of <span className="font-medium">{filteredAll.length}</span>
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setEmpPage(p => Math.max(1, p - 1))} disabled={empPage === 1} className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setEmpPage(p => Math.min(empTotalPages, p + 1))} disabled={empPage === empTotalPages} className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Employer', 'Industry', 'Contact', 'Type', 'Status', 'Joined', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAll.map((e, i) => (
                    <tr key={e.user_id || i} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="relative flex-shrink-0">
                            {/* Profile picture — prominent circle */}
                            <div className="w-9 h-9 rounded-full bg-blue-50 border-2 border-blue-100 flex items-center justify-center overflow-hidden">
                              {e.profile_picture ? (
                                <img src={e.profile_picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <User className="w-4 h-4 text-blue-300" />
                              )}
                            </div>
                            {/* Company logo — tiny badge */}
                            {e.company_logo && (
                              <div className="w-4 h-4 rounded bg-white border border-gray-200 flex items-center justify-center overflow-hidden absolute -bottom-0.5 -right-0.5 shadow-sm">
                                <img src={e.company_logo} alt="" className="w-full h-full object-contain" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{e.contact_person || e.company_name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{e.company_name}</div>
                            {e.avg_rating != null && (
                              <div className="flex items-center gap-1 mt-0.5">
                                {[1,2,3,4,5].map(s => (
                                  <Star key={s} className="w-3 h-3"
                                    fill={s <= Math.round(e.avg_rating!) ? '#FBBF24' : 'none'}
                                    stroke={s <= Math.round(e.avg_rating!) ? '#FBBF24' : '#D1D5DB'} />
                                ))}
                                <span className="text-xs text-gray-400 ml-0.5">{Number(e.avg_rating).toFixed(1)}</span>
                                {e.rating_count != null && (
                                  <span className="text-xs text-gray-400">({e.rating_count})</span>
                                )}
                              </div>
                            )}
                            {e.company_website && (
                              <a href={e.company_website} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-0.5">
                                <Globe className="w-3 h-3" />{e.company_website}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{e.company_industry}</td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-700 flex items-center gap-1">
                          <Mail className="w-3 h-3 text-gray-400" />{e.email}
                        </div>
                        {e.phone && (
                          <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" />{e.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {e.is_industry_partner ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full w-fit">Industry Partner</span>
                            {e.partner_depts.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {e.partner_depts.map(d => (
                                  <span key={d} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded-full">{d}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Standard</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          e.approval_status === 'approved'    ? 'bg-green-100 text-green-700' :
                          e.approval_status === 'pending'     ? 'bg-yellow-100 text-yellow-700' :
                          e.approval_status === 'deactivated' ? 'bg-gray-100 text-gray-500' :
                                                                'bg-red-100 text-red-700'
                        }`}>
                          {e.approval_status.charAt(0).toUpperCase() + e.approval_status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(e.created_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setViewTarget(e)}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors whitespace-nowrap flex items-center gap-1"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() => setDeactivateTarget(e)}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors whitespace-nowrap ${
                              e.approval_status === 'deactivated'
                                ? 'border-green-200 text-green-700 hover:bg-green-50'
                                : 'border-red-200 text-red-600 hover:bg-red-50'
                            }`}
                          >
                            {e.approval_status === 'deactivated' ? 'Reactivate' : 'Deactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Pending tab ── */}
      {tab === 'pending' && (
        <div>
          {groupedPending.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No pending partnership requests.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groupedPending.map(group => {
                const isCollapsed = collapsedCompanies.has(group.company_id)
                const hasMultiple = group.requests.length > 1
                return (
                  <div key={group.company_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Company header */}
                    <div className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{group.company_name}</h3>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              {group.company_industry}
                            </span>
                            {hasMultiple && (
                              <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                                {group.requests.length} department requests
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            {group.contact_person && (
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                {group.contact_person}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" />
                              {group.contact_email}
                            </span>
                            {group.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3.5 h-3.5" />
                                {group.phone}
                              </span>
                            )}
                            {group.company_website && (
                              <a href={group.company_website} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:underline truncate max-w-[200px]">
                                {group.company_website}
                              </a>
                            )}
                          </div>
                        </div>
                        {hasMultiple && (
                          <button
                            onClick={() => toggleCompanyExpand(group.company_id)}
                            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0 self-start"
                          >
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`} />
                            {isCollapsed ? 'Expand' : 'Collapse'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Department request rows — always shown for single, collapsible for multiple */}
                    {(!hasMultiple || !isCollapsed) && (
                      <div className="border-t border-gray-100">
                        {group.requests.map((req, idx) => (
                          <div
                            key={req.partnership_id}
                            className={`px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
                              idx < group.requests.length - 1 ? 'border-b border-gray-100' : ''
                            }`}
                          >
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
                                {req.department_name}
                              </span>
                              <span className="text-xs text-gray-400">
                                Requested {new Date(req.requested_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={() => setApproveTarget(pending.find(p => p.partnership_id === req.partnership_id)!)}
                                disabled={processingIds.has(req.partnership_id)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                                Approve
                              </button>
                              <button
                                onClick={() => openRejectModal(pending.find(p => p.partnership_id === req.partnership_id)!)}
                                disabled={processingIds.has(req.partnership_id)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-medium rounded-lg border border-red-200 transition-colors disabled:opacity-50"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                                Reject
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Collapsed summary — shows dept name chips when card is collapsed */}
                    {hasMultiple && isCollapsed && (
                      <div className="border-t border-gray-100 px-4 sm:px-5 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {group.requests.map(req => (
                            <span key={req.partnership_id} className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
                              {req.department_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── View Employer Modal ── */}
      {viewTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewTarget(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header accent */}
            <div className="h-1.5 w-full bg-gradient-to-r from-blue-700 via-blue-500 to-yellow-400 rounded-t-2xl" />
            <div className="p-6">
              {/* Title row */}
              <div className="flex items-start justify-between mb-5">
                <h2 className="text-lg font-bold text-gray-900">Employer Details</h2>
                <button onClick={() => setViewTarget(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Avatar + name block */}
              <div className="flex items-start gap-4 mb-6">
                {/* Contact person avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-blue-50 border-2 border-blue-100 flex items-center justify-center overflow-hidden">
                    {viewTarget.profile_picture
                      ? <img src={viewTarget.profile_picture} alt="" className="w-full h-full object-cover" />
                      : <User className="w-7 h-7 text-blue-300" />}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-base">{viewTarget.contact_person || viewTarget.company_name}</p>
                  {viewTarget.contact_person && <p className="text-sm text-gray-500">{viewTarget.company_name}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      viewTarget.approval_status === 'approved'    ? 'bg-green-100 text-green-700' :
                      viewTarget.approval_status === 'pending'     ? 'bg-yellow-100 text-yellow-700' :
                      viewTarget.approval_status === 'deactivated' ? 'bg-gray-100 text-gray-500' :
                                                                     'bg-red-100 text-red-700'
                    }`}>
                      {viewTarget.approval_status.charAt(0).toUpperCase() + viewTarget.approval_status.slice(1)}
                    </span>
                    {viewTarget.is_industry_partner && (
                      <span className="text-xs font-semibold bg-yellow-400 text-gray-900 px-2 py-0.5 rounded-full">Industry Partner</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Company logo banner */}
              {viewTarget.company_logo && (
                <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 mb-5">
                  <div className="w-14 h-14 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
                    <img src={viewTarget.company_logo} alt={viewTarget.company_name} className="w-full h-full object-contain p-1.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400">Company Logo</p>
                    <p className="font-semibold text-gray-800 text-sm truncate">{viewTarget.company_name}</p>
                    {viewTarget.company_industry && (
                      <p className="text-xs text-gray-500">{viewTarget.company_industry}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Details grid */}
              <div className="space-y-4 text-sm">

                {/* ── Contact Person ── */}
                <div className="pb-3 border-b border-gray-100">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Contact Person</p>
                  <div className="space-y-2">
                    {viewTarget.position && (
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Position / Title</p>
                          <p className="text-gray-800">{viewTarget.position}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">Email</p>
                        <p className="text-gray-800 break-all">{viewTarget.email}</p>
                      </div>
                    </div>
                    {viewTarget.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Phone</p>
                          <p className="text-gray-800">{viewTarget.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Company Info ── */}
                <div className="pb-3 border-b border-gray-100">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Company</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">Industry</p>
                        <p className="text-gray-800">{viewTarget.company_industry}</p>
                      </div>
                    </div>
                    {viewTarget.company_size && (
                      <div className="flex items-start gap-3">
                        <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Company Size</p>
                          <p className="text-gray-800">{viewTarget.company_size}</p>
                        </div>
                      </div>
                    )}
                    {viewTarget.company_location && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Location</p>
                          <p className="text-gray-800">{viewTarget.company_location}</p>
                        </div>
                      </div>
                    )}
                    {viewTarget.company_website && (
                      <div className="flex items-start gap-3">
                        <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Website</p>
                          <a href={viewTarget.company_website} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:underline break-all">{viewTarget.company_website}</a>
                        </div>
                      </div>
                    )}
                    {viewTarget.company_description && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">About</p>
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{viewTarget.company_description}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Rating ── */}
                <div className="pb-3 border-b border-gray-100">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Alumni Rating</p>
                  {viewTarget.avg_rating != null ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className="w-4 h-4"
                            fill={s <= Math.round(viewTarget.avg_rating!) ? '#FBBF24' : 'none'}
                            stroke={s <= Math.round(viewTarget.avg_rating!) ? '#FBBF24' : '#D1D5DB'} />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-gray-800">{Number(viewTarget.avg_rating).toFixed(1)}</span>
                      {viewTarget.rating_count != null && (
                        <span className="text-sm text-gray-400">({viewTarget.rating_count} {viewTarget.rating_count === 1 ? 'review' : 'reviews'})</span>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No ratings yet</p>
                  )}
                </div>

                {/* ── Account ── */}
                <div className={viewTarget.is_industry_partner && viewTarget.partner_depts.length > 0 ? 'pb-3 border-b border-gray-100' : ''}>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Account</p>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Registered</p>
                      <p className="text-gray-800">{new Date(viewTarget.created_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                </div>

                {/* ── Partnership ── */}
                {viewTarget.is_industry_partner && viewTarget.partner_depts.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Partnership</p>
                    <div className="flex items-start gap-3">
                      <Award className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">Partner Departments</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {viewTarget.partner_depts.map(d => (
                            <span key={d} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-medium">{d}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setViewTarget(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => { setDeactivateTarget(viewTarget); setViewTarget(null) }}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    viewTarget.approval_status === 'deactivated'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'border border-red-200 text-red-600 hover:bg-red-50'
                  }`}
                >
                  {viewTarget.approval_status === 'deactivated' ? 'Reactivate' : 'Deactivate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Deactivate Modal ── */}
      {deactivateTarget && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setDeactivateTarget(null); setDeactivateReason('') }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {deactivateTarget.approval_status === 'deactivated' ? 'Reactivate Account' : 'Deactivate Account'}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">{deactivateTarget.company_name}</p>
              </div>
              <button onClick={() => { setDeactivateTarget(null); setDeactivateReason('') }} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {deactivateTarget.approval_status === 'deactivated'
                ? <><span className="font-medium">{deactivateTarget.company_name}</span> will have their platform access restored.</>
                : <>Deactivating <span className="font-medium">{deactivateTarget.company_name}</span> will prevent them from accessing the platform. This can be reversed at any time.</>
              }
            </p>
            {deactivateTarget.approval_status !== 'deactivated' && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason for deactivation <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={deactivateReason}
                  onChange={e => setDeactivateReason(e.target.value.slice(0, 120))}
                  placeholder="e.g. Fraudulent company, spam postings..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{deactivateReason.length}/120</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setDeactivateTarget(null); setDeactivateReason('') }}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivate}
                disabled={deactivateLoading}
                className={`flex-1 px-4 py-2.5 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                  deactivateTarget.approval_status === 'deactivated'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {deactivateLoading ? 'Processing...' : (
                  deactivateTarget.approval_status === 'deactivated' ? 'Reactivate' : 'Deactivate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve Modal ── */}
      {approveTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setApproveTarget(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Approve Partnership Request</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {approveTarget.company_name} — {approveTarget.department_name}
                </p>
              </div>
              <button onClick={() => setApproveTarget(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              This will grant <span className="font-medium">{approveTarget.company_name}</span> industry partner status with the <span className="font-medium">{approveTarget.department_name}</span> department.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setApproveTarget(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" /> Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejectTarget(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Reject Partnership Request</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {rejectTarget.company_name} — {rejectTarget.department_name}
                </p>
              </div>
              <button onClick={() => setRejectTarget(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Provide a reason for rejecting this request..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRejectTarget(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {rejectLoading ? 'Rejecting...' : (
                  <><XCircle className="w-4 h-4" /> Reject Request</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageEmployers
